import Head from 'next/head';

import { breakpoints } from 'styles/theme/breakpoints';
import { FeatureFlags } from 'featureFlags/constants';
import { findParentProductCategory } from 'productTemplate/utils/findParentProductCategory';
import { getAuctionPTEventSchema } from 'productTemplate/utils/getAuctionPTEventSchema';
import { getBreadcrumbSchema } from 'productTemplate/utils/getBreadcrumbSchema';
import { getEventSchema } from 'productTemplate/utils/getEventSchema';
import { getImageSrcs, IImageSrcs } from 'shared/utils/getImageSrcs';
import { getProductGroupTemplateSchema } from 'productTemplate/utils/getProductGroupTemplateSchema';
import { getProductTemplateCarouselImages } from 'productTemplate/utils/getProductTemplateCarouselImages';
import { getProductTemplateSchema } from 'productTemplate/utils/getProductTemplateSchema';
import { IGmcOffers } from 'shared/types/Offer';
import { IProductTemplate } from 'shared/types/ProductTemplate';
import { isFutureReleaseDate } from 'product-landing-pages/utils/isFutureReleaseDate';
import { LOCALES } from 'localization/constants/configEnums';
import { t } from 'localization/utils/t';
import { useCanonical } from 'layout/canonical/useCanonical';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { slugify } from 'shared/utils/consumerSearchSlugify';
import { CONSUMER_SEARCH_BRANDS_REDIRECTS } from 'brand/enum/consumerSearchRedirects';

const TP = 'productTemplate.components.ProductDetailPageLanding';

interface IProductDetailPageHead {
  locale: LOCALES;
  offers?: IGmcOffers;
  productTemplate: IProductTemplate;
  productTypeFromUrl: string;
}

export const ProductDetailPageHead = ({
  locale,
  offers,
  productTemplate,
  productTypeFromUrl,
}: IProductDetailPageHead) => {
  const {
    brandName,
    color: productColor,
    forAuction: isAuctionPT,
    isFashionProduct,
    mainPictureUrl,
    name: productName,
    productTemplateExternalPictures,
    productType: productTemplateProductType,
    releaseDate,
    silhouette,
    slug,
    status,
    taxonomyLevel1,
    taxonomyLevel2,
    taxonomyLevel3,
  } = productTemplate || {};
  const { pageTitle, pageDescription } = usePageMetadata({
    productTemplate,
    defaultTitle: `${productName || slug} `,
  });
  const productCategoryFromUrl = findParentProductCategory(productTypeFromUrl);
  const isProductGroupSchemaEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_PRODUCT_GROUP_SCHEMA,
  );
  const enablePdpImgAltTag = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_PDP_IMAGE_ALT_TAG);
  const carouselImages = getProductTemplateCarouselImages({
    productTemplate: {
      mainPictureUrl,
      productTemplateExternalPictures,
      productCategory: productCategoryFromUrl,
    },
    enablePdpImgAltTag,
  });
  const firstImage = carouselImages[0];
  const isMissingMainPictureUrl = /missing.png/.test(firstImage || '');
  let imageSrcs: IImageSrcs = { src: '', srcSet: '' };
  let ogImageContent = '';
  if (!isMissingMainPictureUrl) {
    imageSrcs = getImageSrcs({
      crop: !(productTemplateProductType === 'tops' && productColor?.toLowerCase() === 'white'),
      src: firstImage?.mainPictureUrl?.replace('/medium/', '/original/'), // make sure the image source is same one in Image.tsx
      resizeable: true,
    });

    // remove action=crop for og:image tag
    ogImageContent = imageSrcs.src;
    const imageUrlObj = new URL(imageSrcs.src);
    if (imageUrlObj.searchParams.get('action') === 'crop') {
      imageUrlObj.searchParams.delete('action');

      ogImageContent = imageUrlObj.toString();
    }
  }

  const normalizedBrandValue =
    CONSUMER_SEARCH_BRANDS_REDIRECTS[slugify(brandName)] || slugify(brandName);

  const canonical = useCanonical();

  const fashionSchema = [
    {
      id: `/brand/${normalizedBrandValue}`,
      name: brandName,
    },
    taxonomyLevel1 && {
      id: `/${slugify(taxonomyLevel1)}/brand/${normalizedBrandValue}`,
      name: brandName ? `${brandName} ${taxonomyLevel1}` : null,
    },
    taxonomyLevel1 &&
      taxonomyLevel2 && {
        id: `/${slugify(taxonomyLevel1)}/brand/${normalizedBrandValue}/${slugify(taxonomyLevel2)}`,
        name: brandName ? `${brandName} ${taxonomyLevel2}` : null,
      },
  ];

  if (isFashionProduct && taxonomyLevel3 && taxonomyLevel1 && taxonomyLevel2) {
    fashionSchema.push({
      id: `/${slugify(taxonomyLevel1)}/brand/${normalizedBrandValue}/${slugify(
        taxonomyLevel2,
      )}/${slugify(taxonomyLevel3)}`,
      name: brandName ? `${brandName} ${taxonomyLevel3}` : null,
    });
  }

  const sneakerSchema = [
    {
      id: `/brand/${normalizedBrandValue}`,
      name: brandName,
    },
    {
      id: `/sneakers/brand/${normalizedBrandValue}`,
      name: brandName
        ? t(`${TP}.sneakerSchemaSneakers`, '{brandName} Sneakers', { brandName })
        : null,
    },
  ];

  if (taxonomyLevel2 && taxonomyLevel2.toLowerCase() !== 'sneakers') {
    sneakerSchema.push({
      id: `/sneakers/brand/${normalizedBrandValue}/${slugify(taxonomyLevel2)}`,
      name: taxonomyLevel2,
    });
  }

  if (silhouette) {
    sneakerSchema.push({
      id: `/sneakers/silhouette/${slugify(silhouette)}`,
      name: silhouette,
    });
  }

  return (
    <Head>
      <title>{pageTitle}</title>
      <meta property="og:title" content={pageTitle} key="og:title" />
      <meta name="description" content={pageDescription} key="description" />
      <meta property="og:description" content={pageDescription} key="og:description" />
      <meta name="robots" content="max-image-preview:large" />
      {!isMissingMainPictureUrl && ogImageContent && (
        <meta property="og:image" content={ogImageContent} key="og:image" />
      )}
      {status === 'inactive' && <meta name="robots" content="noindex" />}
      {offers && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: isProductGroupSchemaEnabled
              ? getProductGroupTemplateSchema({
                  productTemplate,
                  offers: offers?.offerData,
                  canonical,
                })
              : getProductTemplateSchema(productTemplate, offers?.offerData),
          }}
        />
      )}
      {productTemplateProductType === 'sneakers' && isFutureReleaseDate(releaseDate) && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: getEventSchema(productTemplate, locale),
          }}
        />
      )}

      {isAuctionPT && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: getAuctionPTEventSchema(productTemplate),
          }}
        />
      )}

      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: getBreadcrumbSchema(isFashionProduct ? fashionSchema : sneakerSchema, locale),
        }}
      />

      {!isMissingMainPictureUrl && imageSrcs.src && (
        <link
          rel="preload"
          as="image"
          href={imageSrcs.src}
          imageSrcSet={imageSrcs.srcSet}
          imageSizes={`(max-width: ${breakpoints.mediumSmall}px) 50vw, (max-width: ${breakpoints.mediumLarge}px) 350px, 30vw`} /* eslint-disable-line react/no-unknown-property */
          type="image/webp"
          fetchpriority="high" /* eslint-disable-line react/no-unknown-property */
        />
      )}
    </Head>
  );
};
